import { VALID_PLACEMENTS } from '../Header/Dropdown/Drawer/StyledHeaderDrawer';
import { themeMerge } from '../Utils';

import IThemeInterface, { breakPointSizes, edgeSize, zIndices } from './common';

const buttonFocusOverride: Partial<IThemeInterface['button']['base']> = {
  outline: { offset: '2.5px' },
};

const theme: IThemeInterface = themeMerge({
  name: 'avant',

  global: {
    breakPointSizes,

    colors: {
      gray700: '#838E93',
      gray600: '#919A9F',
      gray500: '#A7AEB2',
      gray400: '#B2B8BB',
      gray300: '#C3C7CA',
      gray200: '#DCDFE0',
      gray100: '#EDEEEF',
      gray50: '#F6F7F7',

      black: '#4F5E66',
      error: '#D42E1C',
      error10: '#FDF6F5',
      focus: '#1F71B4',
      info: '#2075BC',
      link: '#2075BC',
      primary: '#2075BC',
      primary120: '#18598D',
      primary50: '#91C0E6',
      primary10: '#F4F9FC',
      primaryHover: '#1F71B4',
      secondary: '#00B68A',
      success: '#007A5C',
      success10: '#E6F7F3',
      warning: '#8A6D3B',
      warning10: '#FCF8E3',
      white: '#fff',
    },

    edgeSize,

    elevation: {
      '1x': { color: 'rgba(120, 120, 120, 0.16)', shadow: '0px 2px 4px 2px' },
      '2x': { color: 'rgba(79, 94, 102, 0.16)', shadow: '0px 2px 4px 2px'},
    },

    font: {
      family: { opensans: 'opensans', effra: 'effra' },
      size: {
        heroLarge: '3.6rem',
        heroLargeMobile: '3rem',
        heroMedium: '2.4rem',
        heroSmall: '2rem',
        body: '1.6rem',
        bodyLarge: '1.8rem',
        bodyMedium: '1.4rem',
        bodySmall: '1.2rem',
      },
    },

    outline: {
      color: 'focus',
      size: 'double',
      style: 'solid',
    },

    zIndex: zIndices,
  },

  banner: {
    info: {
      backgroundColor: 'primary10',
      border: {
        color: 'primary10',
      },
      inlineLink: {
        color: 'black',
        hoverColor: 'black',
        weight: '700',
      },
      closeIcon: {
        color: 'black'
      },
      padding: '2x',
      font: { color: 'black' },
    },
    warning: {
      backgroundColor: 'warning10',
      inlineLink: {
        color: 'black',
        hoverColor: 'black',
        weight: '700',
      },
      closeIcon: {
        color: 'black'
      },
    },
    success: {
      backgroundColor: 'success10',
      border: { color: 'success10' },
      inlineLink: {
        color: 'black',
        hoverColor: 'black',
        weight: '700',
      },
      closeIcon: {
        color: 'black'
      },
    },
    error: {
      backgroundColor: 'error10',
      border: { color: 'error10' },
      inlineLink: {
        color: 'black',
        hoverColor: 'black',
        weight: '700',
      },
      closeIcon: {
        color: 'black'
      },
    },
  },

  barProgress: {
    borderRadius: '100px',
    track: {
      height: '0.8rem',
      color: {
        base: 'gray400',
        progress: 'success'
      }
    }
  },

  button: {
    base: {
      background: 'primary',
      border: 'none',
      borderRadius: 'double',
      shadow: 'none',
      font: {
        color: 'white',
        size: 'body',
        letterSpacing: 'none',
        lineHeight: '1.5',
        textTransform: 'none',
        weight: '700'
      },
      icons: { mobileHidden: true },
      outline: 'none',
      padding: { horizontal: '2x', vertical: '1.5x' },
      width: { min: '11.5em' },
      loadingIconInsideButton: true,
      spinner: {
        color: {
          animatedOutline: 'white',
          circleOutline: 'primary120',
        },
      },
      additionalStyles: ''
    },

    overrides: {
      primary: {
        state: {
          disabled: {
            background: 'gray200',
          },
          hover: {
            background: 'primaryHover',
          },
          focus: buttonFocusOverride,
        },
      },

      secondary: {
        base: {
          background: 'white',
          border: { color: 'primary', size: '1px'},
          font: { color: 'primary' },
          padding: { vertical: '11px' },
          loadingIconInsideButton: true,
          spinner: {
            color: {
              animatedOutline: 'primary',
              circleOutline: 'gray200',
            },
          },
        },
        state: {
          disabled: {
            border: { color: 'gray200' },
            font: { color: 'gray200' },
          },
          hover: {
            background: 'primary10',
          },
          focus: buttonFocusOverride,
        }
      },

      link: {
        base: {
          background: 'none',
          font: {
            color: 'link',
            textDecoration: 'underline',
            size: 'body',
            lineHeight: '1.5',
            weight: '400',
          },
          border: { size: 'none' },
          padding: 'none',
          width: { base: 'auto', min: 'auto'},
        },
        state: {
          hover: {
            font: { color: 'primaryHover' },
          },
          focus: {
            font: { color: 'primaryHover' },
            outline: { color: 'focus' },
          },
          disabled: { font: { color: 'gray200' } }
        }
      },

      text: {
        base: {
          background: 'none',
          font: {
            color: 'link',
            size: 'body',
            lineHeight: '1.5',
            weight: '400',
          },
          border: { size: 'double', color: 'transparent' },
          padding: { vertical: 'none' },
        },
        state: {
          hover: {
            font: { color: 'primaryHover' },
          },
          focus: {
            border: { color: 'primaryHover' },
          },
          disabled: { font: { color: 'gray200' } }
        },
      },
    },
  },

  containerSize: {
    full: '100%',
    narrow: '51.2rem', // 512px
    'narrow-2': '70.4rem', // 704px
    wide: '89.6rem', // 896px
    max: '128rem', // 1280px
  },

  circleProgress: {
    backgroundPathColor: 'gray400',
    percentagePathColor: 'success',
    label: {
      percentage: {
        number: {
          size: 'heroLarge',
          lineHeight: '1.25',
          color: 'black',
          family: 'effra',
        },
        sign: {
          size: 'heroMedium',
          weight: '700',
          verticalAlign: 'super',
        }
      },
      content: {
        size: 'bodyMedium',
        color: 'gray600',
      },
    },
  },

  datePicker: {
    input: {
      // tslint:disable:max-line-length
      icon: (color: string) => `"data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='${encodeURIComponent(color)}' d='M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1c-.553 0-1 .447-1 1v12c0 .553.447 1 1 1h14c.553 0 1-.447 1-1V3c0-.553-.447-1-1-1zm-1 12H2V6h12v8z'/%3E%3C/svg%3E"`,
      iconColor: {
        base: 'black'
      }
    },
    monthLabel: {
      fontSize: 'heroSmall',
      fontWeight: '700',
    },
    weekdayLabel: {
      color: 'black',
      fontSize: 'bodySmall',
      fontWeight: '700',
      border: { side: 'bottom', color: 'gray200' },
      padding: { top: '2.5x' }
    },
    dayCell: {
      fontSize: 'body',
      borderRadius: { topLeft: '2px', bottomLeft: '2px', topRight: '2px', bottomRight: '2px' },
      selected: {
        backgroundColor: 'primary',
        color: 'white',
        fontWeight: '700'
      },
      hover: {
        backgroundColor: 'primary',
        color: 'white'
      },
      disabled: {
        color: 'gray500'
      },
      due: {
        backgroundColor: 'error10',
        color: 'error',
        border: { color: 'error', size: '1px' },
        fontWeight: '700',
        dueText: {
          fontSize: 'bodySmall',
          lineHeight: '1',
        }
      },
      padding: { top: '1x', left: '1x', right: '1x', bottom: '2.5x' },
      leadingZeros: true,
    },
    dayPickerProps: {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      weekdaysShort: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
    },
    overlay: {
      backgroundColor: 'white',
      border: { size: '.25px', color: 'white' },
      boxShadow: '2x',
      borderRadius: '5px',
    },
    focusRing: {
      outline: {
        color: 'focus',
        size: 'double',
        style: 'solid',
        offset: 'double'
      },
    },
  },

  descriptionList: {
    term: {
      family: 'opensans',
      weight: '700',
      color: 'black',
      size: 'body',
      margin: { right: '3x' },
      lineHeight: '1.5',
    },

    detail: {
      family: 'opensans',
      weight: '400',
      color: 'black',
      size: 'body',
      lineHeight: '1.5',
    },

    variant: {
      large: {
        term: {
          weight: '700',
          lineHeight: '1.45',
          size: 'bodyMedium',
        },
        detail: {
          weight: '700',
          size: 'heroLarge',
          lineHeight: '1.35',
        }
      },

      small: {
        term: {
          weight: '700',
          lineHeight: '1.45',
          size: 'bodyMedium',
        },
        detail: {
          weight: '700',
          size: 'heroMedium',
          lineHeight: '1.35'
        },
      },
    },
  },

  form: {
    checkbox: {
      backgroundColor: 'primary',
      border: { color: 'gray700' },
      icon: {
        checkedIconType: 'checkbox-done',
        size: '1.5x',
        color: 'white'
      },
      label: {
        font: { color: 'black', lineHeight: '1', size: 'body' }
      },
      round: 'double',
      size: '2x',
      margin: { right: '1x' },

      state: {
        checked: {
          backgroundColor: 'primary',
          border: { color: 'primary' },
        },

        disabled: {
          backgroundColor: 'gray100',
          border: { color: 'gray300' },
          label: { font: { color: 'gray500' }},
          shadow: { color: 'gray100', shadow: '0 0 0 4px' },
        },

        disabledChecked: {
          backgroundColor: 'gray100',
          border: { color: 'gray300' },
          icon: { color: 'gray300' },
        },

        error: {
          border: { color: 'error' },
          shadow: { color: 'error', shadow: '0 0 0 4px' },
        },

        focus: {
          focusPosition: 'labelAndIndicator',
          outline: { offset: '5px' },
          shadow: { color: 'gray100', shadow: '0 0 0 4px' },
        },

        hover: {
          border: { color: 'primary' },
          shadow: { color: 'gray100', shadow: '0 0 0 4px' }
        },
      },
    },

    layout: {
      wrapper: {
        row: { margin: { bottom: '3x' } },
        stackRow: {
          margin: {
            base: { right: '0' },
            small: { right: '3x' },
          },
        },
      },
    },

    helperText: {
      font: {
        color: {
          default: 'black',
          disabled: 'black',
          error: 'black',
          readOnly: 'black',
        },
        size: 'bodyMedium',
        weight: '400',
        lineHeight: '1.4',
      },
      margin: { top: 'quadruple' },
    },

    input: {
      backgroundColor: 'white',
      border: { color: 'gray700', size: 'single', side: 'all' },
      iconColor: 'black',
      font: { color: 'black', lineHeight: '1.5' },
      padding: { horizontal: '1.5x', top: '0.875x', bottom: '0.875x' },
      placeholder: 'black',
      round: 'triple',

      state: {
        hover: {
          border: { color: 'primary' },
        },

        focus: {
          border: { color: 'focus', size: 'single' },
          shadow: { shadow: '0 0 0 0.1rem', color: 'focus', inset: true },
        },

        error: {
          border: { color: 'error' },
        },

        disabled: {
          border: { color: 'gray700' },
          font: { color: 'gray700' },
          backgroundColor: 'gray50'
        },

        readOnly: {
          border: { color: 'gray700' },
          font: { color: 'gray500' }
        },
      },
    },

    label: {
      font: {
        color: {
          default: 'black',
          disabled: 'black',
          readOnly: 'black',
        },
        size: 'bodyMedium',
        weight: '700',
      },
      margin: { bottom: 'quadruple' },
    },

    legend: {
      font: {
        size: 'bodyMedium',
        color: 'black',
        weight: '700',
      },
      margin: { bottom: '2x' },
    },

    radio: {
      badge: {
        backgroundColor: 'white',
        border: { color: 'gray700' },
        padding: { vertical: '1.5x', horizontal: '2.5x' },
        round: 'triple',
        width: { min: '4.8125em' },
        shadow: 'none',
        font: { color: 'black', weight: '700' },

        state: {
          checked: {
            backgroundColor: 'primary10',
            border: { color: 'primary' },
            font: { color: 'primary' },
          },
          disabled: {
            backgroundColor: 'gray100',
            border: { color: 'gray300' },
            font: { color: 'gray500' },
          },
          disabledChecked: {
            backgroundColor: 'gray300',
            border: { color: 'gray300' },
            font: { color: 'white' },
          },
          error: {
            border: { color: 'error' },
          },
          focus: {
            shadow: { color: 'focus', inset: true, shadow: '0 0 0 1px' },
          },
          hover: {
            border: { color: 'primary' },
          },
        },
      },

      button: {
        backgroundColor: 'white',
        checkedStyle: 'solid',
        checkedBgColor: 'primary',
        border: { color: 'gray700' },
        label: { font: { color: 'black', lineHeight: '1.2', size: 'body' }},
        size: '2.5x',

        state: {
          checked: {
            shadow: { color: 'white', inset: true, shadow: '0 0 0 2.75px' },
            border: { color: 'primary' },
            label: { font: { color: 'primary' }},
          },
          disabled: {
            border: { color: 'gray300' },
            backgroundColor: 'gray100',
            label: { font: { color: 'gray500' }},
          },
          disabledChecked: {
            backgroundColor: 'gray300',
            shadow: { color: 'gray100', inset: true, shadow: '0 0 0 2.75px' },
            border: { color: 'gray300' },
          },
          error: {
            border: { color: 'error' },
          },
          focus: {
            focusPosition: 'labelAndIndicator',
          },
          hover: {
            border: { color: 'primary' },
          },
        },
      },

      card: {
        backgroundColor: 'white',
        border: { color: 'gray200' },
        round: 'triple',
        shadow: '1x',

        state: {
          checked: {
            backgroundColor: 'primary10',
            border: { color: 'focus' },
          },
          focus: {
            border: { size: 'double', color: 'focus' },
          },
          hover: {
            border: { color: 'primary' },
          },
          disabled: {
            backgroundColor: 'gray50',
            border: { size: 'single', color: 'gray50' },
            shadow: '1x',
          },

          error: {
            text: {
              margin: { top: '3x' }
            }
          },
        },
      },

      toggle: {
        label: {
          color: 'black',
          size: 'body',
        },

        button: {
          padding: { vertical: '0.75x', horizontal: '0.75x' },
          border: { color: 'gray700' },
          backgroundColor: 'transparent',
        },

        slidingIndicator: {
          size: '1x',
          color: 'gray700',
        },

        state: {
          checked: {
            label: { color: 'black' },
            button: {
              border: { color: 'primary' },
              backgroundColor: 'primary',
            },
            slidingIndicator: {
              color: 'white',
            },
          },

          disabled: {
            label: { color: 'gray500' },
            button: {
              border: { color: 'gray300' },
              backgroundColor: 'gray100',
            },
            slidingIndicator: {
              color: 'gray300',
            },
          },

          disabledChecked: {
            label: { color: 'gray500' },
            button: {
              border: { color: 'gray300' },
              backgroundColor: 'gray300',
            },
            slidingIndicator: {
              color: 'gray100',
            },
          },

          focus: {
            label: { outline: { offset: 'double'} },
          },

          hover: {
            button: {
              border: { color: 'primary' },
            },
            slidingIndicator: {
              color: 'primary',
            },
          },
        },
      },
    },

    select: {
      icon: {
        type: 'select-input-arrow',
        size: '2x',
      },
    },

    slider: {
      outline: { color: 'focus' },
      thumb: {
        color: { default: 'primary', disabled: 'gray500' },
        height: 36,
        width: 36,
      },
      track: {
        color: {
          base: { default: 'gray200', disabled: 'gray100' },
          progress: { default: 'primary50', disabled: 'gray300' }
        },
        thickness: 8,
        borderRadius: '4px',
      },
      unitLabels: {
        color: { default: 'black', disabled: 'gray700' },
        margin: { top: '2x' },
      }
    },

    textarea: {
      backgroundColor: 'white',
      border: { color: 'gray600' },
      font: { color: 'black' },
      padding: { top: '1.5x', bottom: '1.5x', horizontal: '2x' },
      round: 'triple',

      state: {
        hover: {
          border: { color: 'primary' },
        },

        focus: {
          shadow: { color: 'focus', shadow: '0 0 0 1px', inset: true },
          border: { color: 'focus' },
        },

        error: {
          border: { color: 'error' },
        },

        disabled: {
          backgroundColor: 'gray50',
          border: { color: 'gray700' },
          font: { color: 'gray700' },
        },
      },
    },
  },

  heading: {
    default: {
      color: 'black',
      family: 'effra',
      lineHeight: '1.35',
      weight: '700'
    },

    level: {
      h1: {
        size: { base: 'heroLargeMobile', medium: 'heroLarge' },
      },

      h2: {
        size: { base: 'heroSmall', medium: 'heroMedium' },
      },

      h3: {
        family: 'optimist',
        size: { base: 'bodyLarge', medium: 'heroSmall' },
        lineHeight: '1.4',
      },

      h4: {/* level specific styles */ },
      h5: {/* level specific styles */ },
      h6: {/* level specific styles */ },
    },
  },

  layout: {
    header: {
      border: {
        size: '1px',
        side: 'bottom',
        color: 'gray200'
      },
      padding: {
        base: { horizontal: '3x' },
        small: { horizontal: '10x' },
      },
      height: { base: '6.4rem', small: '8rem' },

      dropdown: {
        shadow: '2x',
        width: { base: '100%', small: '24rem' },
        backgroundColor: 'white',
        border: 'none',
        borderRadius: { base: 'none', small: '1x' },
        icon: {
          desktop: {
            size: '2.75x',
            color: 'black',
            type: 'arrow-bottom',
          },
          mobile: {
            open: {
              type: 'menu',
              color: 'black',
              size: '2.25x'
            },
            close: {
              size: '2.25x',
              color: 'black',
              type: 'close'
            }
          }
        },
        padding: { vertical: '1x' },

        button: {
          font: {
            color: 'black',
            size: 'body',
            lineHeight: '1.5',
          },
        },

        link: {
          divider: {
            placement: VALID_PLACEMENTS.lastChildOnly,
            height: '1px',
            backgroundColor: 'gray200',
            margin: { vertical: '1x' },
            width: { base: '100%', small: 'calc(100% - 3.2rem)'}
          },
          border: { size: 'double', color: 'transparent' },
          font: {
            color: 'black',
            size: 'body',
            lineHeight: '1.5',
            align: { base: 'center', small: 'left' },
          },

          padding: { vertical: '0.75x', horizontal: '2x' },

          state: {
            hover: {
              font: { color: 'link' },
            },
            focus: { border: { size: 'double', color: 'focus' }}
          },
        },
      },

      link: {
        font: {
          color: 'black',
          lineHeight: '1.5',
          size: 'body',
        },
        margin: {
          right: '3x',
        },
        state: {
          hover: {
            font: {
              color: 'link',
            },
          },
        },
      },
    },
  },

  link: {
    inline: {
      state: {
        hover: { color: 'primaryHover' },
      },
    },
  },

  list: {
    margin: { left: '2x' },

    listItem: {
      common: {
        margin: { bottom: '1x' },
        font: {
          lineHeight: '1.5',
          size: 'body',
          color: 'black',
          weight: '400'
        },
      },

      bullet: {
        backgroundColor: 'black',
        listStyleSpacing: '2.6rem',
        size: '0.75x',
      },

      icon: {
        listStyleSpacing: '1.5x',
        size: '2.5x',
        type: 'done',
        color: 'black',
      },

      number: {
        listStyleSpacing: '3.4rem',
        font: {
          weight: '400',
          color: 'black',
        },
      },
    },
  },

  modal: {
    overlay: {
      backgroundColor: '#000',
      padding: {
        base: { top: '3x' },
        medium: { top: '11x' },
      },
    },
    card: {
      backgroundColor: 'white',
      border: { color: 'white' },
      padding: {
        base: { vertical: '6x', horizontal: '3x' },
        medium: { horizontal: '6x', vertical: '7x'},
      },
      round: 'quadruple',
      shadow: '2x',
    },
    closeIcon: {
      type: 'alert-close',
      size: '1.75x',
      color: 'black',
      padding: '2.25x'
    },
  },

  spinner: {
    size: '5em',
    iconStrokeWidth: 1,
    withOutline: true,
    color: {
      animatedOutline: 'black',
      circleOutline: 'gray200',
    },
  },

  table: {
    border: 'none',

    tableRow: {
      height: '1px',
    },

    tableHeader: {
      backgroundColor: 'gray50',
      rowDivider: 'gray200',
      padding: {
        base: { vertical: '1x', horizontal: '1.5x' },
        small: { vertical: '1x', horizontal: '2x' },
      },
      font: {
        size: 'bodyMedium',
        lineHeight: '1.125',
        textTransform: 'uppercase',
      },
    },

    tableBody: {
      rowDivider: 'gray200',
      padding: {
        base: { vertical: 'quadruple', horizontal: '1.5x' },
        small: { vertical: '2.5x', horizontal: '2x' },
      },
      font: {
        size: 'body',
        lineHeight: '1.25',
      },
    },
  },

  tooltip: {
    activationType: 'automatic',
    pane: {
      background: 'white',
      padding: { vertical: '1.5x', horizontal: '2x' },
      shadow: '2x',
      round: 'double',
      verticalOffset: '15%',
      helperTextVerticalOffset: '-3px',
      position: 'top',

      font: {
        size: 'bodyMedium',
        color: 'black',
      },
    },

    icon: {
      size: '1.6rem',
      color: 'primary',
      hover: 'primaryHover',
      margin: { left: 'quadruple' },
    },
  },

  typography: {
    text: {
      body: {
        default: {
          color: 'black',
          lineHeight: '1.5',
        },
        large: { lineHeight: '1.35' },
        medium: { lineHeight: '1.75' },
        small: { lineHeight: '1.5' },
      },
    },

    pill: {
      default: {
        padding: { vertical: '4px', horizontal: '1x' },
        round: 'double',
        font: {
          transform: 'uppercase',
          color: 'white',
          size: 'bodySmall',
          lineHeight: '1.4',
          weight: '700',
        },
      },
      neutral: {
        font: { color: 'black' },
        backgroundColor: 'gray200'
      },
      success: { backgroundColor: 'success' },
      warning: { backgroundColor: 'warning' },
      error: { backgroundColor: 'error' },
      info: { backgroundColor: 'primary' },
    },
  },
});

export default theme;
